import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Building2,
  UserCircle2,
  Check,
  MapPin,
  Brain,
} from "lucide-react";
import { toast } from "sonner";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a4143582-6eb1-4e43-b482-b7eb9ef3a7f9/images/4b5c6aad77a5398da2e0382be1a07b3bfa387cc4fe1d5f78c42b17d6f62e3d3a.png";

const StepBadge = ({ index, label, active, done }) => (
  <div className="flex items-center gap-2">
    <span
      className={`h-6 w-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
        done
          ? "bg-[#4A5D4E] text-white"
          : active
          ? "bg-[#1A1D1A] text-white"
          : "bg-[#E5E2DC] text-[#8A9188]"
      }`}
    >
      {done ? <Check strokeWidth={2.5} size={12} /> : index + 1}
    </span>
    <span
      className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
        active || done ? "text-[#1A1D1A]" : "text-[#8A9188]"
      }`}
    >
      {label}
    </span>
  </div>
);

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useT();
  const [searchParams] = useSearchParams();
  // Embed deep-links: ?institute=<id>             → start at step 2 (coach picker)
  //                   ?institute=<id>&coach=<id>  → start at step 3 (details)
  const lockedInstituteId = searchParams.get("institute") || "";
  const lockedCoachId = searchParams.get("coach") || "";
  const isLockedInstitute = Boolean(lockedInstituteId);
  const isLockedCoach = Boolean(lockedInstituteId && lockedCoachId);

  const [step, setStep] = useState(0); // 0=institute, 1=coach, 2=details
  const [institutes, setInstitutes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loadingInst, setLoadingInst] = useState(true);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const [filterInst, setFilterInst] = useState("");
  const [filterCoach, setFilterCoach] = useState("");

  const [institute, setInstitute] = useState(null); // {id, name}
  const [coach, setCoach] = useState(null); // {id, name, email}
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load the public institute list. When the URL deep-links to a specific
  // institute (embed mode) we also pre-select it and fetch its coaches so
  // we can jump directly to step 2 / step 3.
  useEffect(() => {
    const load = async () => {
      try {
        const { data: insts } = await api.get("/public/institutes");
        setInstitutes(insts);
        if (lockedInstituteId) {
          const inst = insts.find((i) => i.id === lockedInstituteId);
          if (inst) {
            setInstitute(inst);
            setLoadingCoaches(true);
            try {
              const { data: cs } = await api.get(
                `/public/institutes/${inst.id}/coaches`
              );
              setCoaches(cs);
              if (lockedCoachId) {
                const c = cs.find((x) => x.id === lockedCoachId);
                if (c) {
                  setCoach(c);
                  setStep(2);
                } else {
                  setStep(1);
                }
              } else {
                setStep(1);
              }
            } finally {
              setLoadingCoaches(false);
            }
          }
        }
      } catch (e) {
        toast.error(t("register.error.load_institutes"));
      } finally {
        setLoadingInst(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickInstitute = async (inst) => {
    setInstitute(inst);
    setCoach(null);
    setLoadingCoaches(true);
    try {
      const { data } = await api.get(`/public/institutes/${inst.id}/coaches`);
      setCoaches(data);
      setStep(1);
    } catch (e) {
      toast.error(t("register.error.load_coaches"));
    } finally {
      setLoadingCoaches(false);
    }
  };

  const pickCoach = (c) => {
    setCoach(c);
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/register", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        institute_id: institute.id,
        coach_id: coach.id,
      });
      if (data?.token) localStorage.setItem("nsp_token", data.token);
      setUser(data.user);
      toast.success(t("register.welcome_toast", { name: data.user.name.split(" ")[0] }));
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("register.error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const visibleInstitutes = institutes.filter((i) =>
    !filterInst
      ? true
      : i.name.toLowerCase().includes(filterInst.toLowerCase()) ||
        (i.location || "").toLowerCase().includes(filterInst.toLowerCase())
  );
  const visibleCoaches = coaches.filter((c) =>
    !filterCoach
      ? true
      : c.name.toLowerCase().includes(filterCoach.toLowerCase()) ||
        c.email.toLowerCase().includes(filterCoach.toLowerCase()) ||
        (c.location || "").toLowerCase().includes(filterCoach.toLowerCase())
  );

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-[#F9F8F6]"
      data-testid="register-page"
    >
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-start justify-between gap-4 mb-12">
            <div className="flex items-center gap-3">
              <Sparkles strokeWidth={1.5} className="text-[#B37A5F]" size={22} />
              <div className="leading-tight">
                <span className="font-heading text-xl tracking-tight font-semibold text-[#1A1D1A] block">
                  NeuroSophia
                </span>
                <span className="text-xs italic text-[#5C635A]" data-testid="brand-tagline">
                  {t("brand.tagline")}
                </span>
              </div>
            </div>
            <LanguageSwitcher variant="light" />
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold mb-4">
            {t("register.title")}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#1A1D1A] leading-[1.1]">
            {t("register.headline_a")}{" "}
            <span className="font-semibold">{t("register.headline_b")}</span>
          </h1>
          <p className="mt-4 text-[#5C635A] leading-relaxed">{t("register.lead")}</p>

          <div
            className="mt-10 flex items-center gap-3 flex-wrap"
            data-testid="register-steps"
          >
            <StepBadge index={0} label={t("register.step.institute")} active={step === 0} done={step > 0} />
            <span className="text-[#E5E2DC]">·</span>
            <StepBadge index={1} label={t("register.step.coach")} active={step === 1} done={step > 1} />
            <span className="text-[#E5E2DC]">·</span>
            <StepBadge index={2} label={t("register.step.details")} active={step === 2} done={false} />
          </div>

          {/* Step 0: pick institute */}
          {step === 0 && (
            <div className="mt-10 space-y-4" data-testid="register-step-institute">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                {t("register.q_institute")}
              </Label>
              <Input
                type="text"
                value={filterInst}
                onChange={(e) => setFilterInst(e.target.value)}
                placeholder={t("register.institute_search_placeholder")}
                data-testid="register-institute-search"
                className="h-11 rounded-lg bg-white border-[#E5E2DC]"
              />
              <div
                className="max-h-[420px] overflow-y-auto rounded-xl border border-[#E5E2DC] bg-white divide-y divide-[#E5E2DC]"
                data-testid="register-institute-list"
              >
                {loadingInst ? (
                  <p className="px-5 py-8 text-sm text-[#8A9188]">{t("register.loading_institutes")}</p>
                ) : visibleInstitutes.length === 0 ? (
                  <p className="px-5 py-8 text-sm text-[#8A9188]">{t("register.no_matches")}</p>
                ) : (
                  visibleInstitutes.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => pickInstitute(i)}
                      data-testid={`register-institute-option-${i.id}`}
                      className="w-full text-left px-5 py-4 hover:bg-[#F2F0EA] transition-colors flex items-center gap-3 group"
                    >
                      <Building2 strokeWidth={1.5} className="text-[#B37A5F] shrink-0" size={18} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1A1D1A] truncate">{i.name}</p>
                        {i.location && (
                          <p
                            className="text-xs text-[#8A9188] truncate flex items-center gap-1 mt-0.5"
                            data-testid={`register-institute-option-${i.id}-location`}
                          >
                            <MapPin strokeWidth={1.5} size={11} />
                            {i.location}
                          </p>
                        )}
                      </div>
                      <ArrowRight
                        strokeWidth={1.5}
                        size={16}
                        className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 1: pick coach */}
          {step === 1 && (
            <div className="mt-10 space-y-4" data-testid="register-step-coach">
              {!isLockedInstitute && (
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  data-testid="register-back-to-institute"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold"
                >
                  <ArrowLeft strokeWidth={1.5} size={14} />
                  {institute?.name}
                </button>
              )}
              {isLockedInstitute && (
                <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold">
                  <Building2 strokeWidth={1.5} size={14} />
                  {institute?.name}
                </p>
              )}
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                {t("register.q_coach")}
              </Label>
              <Input
                type="text"
                value={filterCoach}
                onChange={(e) => setFilterCoach(e.target.value)}
                placeholder={t("register.coach_search_placeholder")}
                data-testid="register-coach-search"
                className="h-11 rounded-lg bg-white border-[#E5E2DC]"
              />
              <div
                className="max-h-[420px] overflow-y-auto rounded-xl border border-[#E5E2DC] bg-white divide-y divide-[#E5E2DC]"
                data-testid="register-coach-list"
              >
                {loadingCoaches ? (
                  <p className="px-5 py-8 text-sm text-[#8A9188]">{t("register.loading_coaches")}</p>
                ) : visibleCoaches.length === 0 ? (
                  <p className="px-5 py-8 text-sm text-[#8A9188]">
                    {coaches.length === 0 ? t("register.no_coaches_yet") : t("register.no_matches")}
                  </p>
                ) : (
                  visibleCoaches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pickCoach(c)}
                      data-testid={`register-coach-option-${c.id}`}
                      className="w-full text-left px-5 py-4 hover:bg-[#F2F0EA] transition-colors flex items-center gap-3 group"
                    >
                      <UserCircle2 strokeWidth={1.5} className="text-[#B37A5F] shrink-0" size={18} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1A1D1A] truncate">{c.name}</p>
                        <p className="text-xs text-[#8A9188] truncate">{c.email}</p>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {c.location && (
                            <p
                              className="text-xs text-[#8A9188] truncate flex items-center gap-1"
                              data-testid={`register-coach-option-${c.id}-location`}
                            >
                              <MapPin strokeWidth={1.5} size={11} />
                              {c.location}
                            </p>
                          )}
                          {c.brain_type != null && (
                            <p
                              className="text-xs text-[#8A9188] flex items-center gap-1"
                              data-testid={`register-coach-option-${c.id}-brain-type`}
                            >
                              <Brain strokeWidth={1.5} size={11} />
                              {t("register.coach_brain_type", { n: c.brain_type })}
                            </p>
                          )}
                        </div>
                      </div>
                      <ArrowRight
                        strokeWidth={1.5}
                        size={16}
                        className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: details */}
          {step === 2 && (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-step-details">
              {!isLockedCoach && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="register-back-to-coach"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold"
                >
                  <ArrowLeft strokeWidth={1.5} size={14} />
                  {coach?.name} · {institute?.name}
                </button>
              )}
              {isLockedCoach && (
                <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold">
                  <UserCircle2 strokeWidth={1.5} size={14} />
                  {coach?.name} · {institute?.name}
                </p>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                  {t("register.name_label")}
                </Label>
                <Input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="register-name-input"
                  className="h-11 rounded-lg bg-white border-[#E5E2DC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                  {t("register.email_label")}
                </Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="register-email-input"
                  className="h-11 rounded-lg bg-white border-[#E5E2DC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                  {t("register.password_label")}
                </Label>
                <Input
                  required
                  type="password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="register-password-input"
                  className="h-11 rounded-lg bg-white border-[#E5E2DC]"
                />
              </div>

              {error && (
                <p
                  className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                  data-testid="register-error"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="register-submit-button"
                className="w-full h-12 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white font-medium tracking-tight"
              >
                {submitting ? t("register.creating") : t("register.create_and_start")}
              </Button>
            </form>
          )}

          <p className="mt-10 text-sm text-[#5C635A]">
            {t("register.already_account")}{" "}
            <Link
              to="/login"
              data-testid="register-sign-in-link"
              className="font-semibold text-[#4A5D4E] hover:underline"
            >
              {t("register.sign_in")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right: hero image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BG_IMG}
          alt="Wellness organic shapes"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  );
}
